import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Paperclip, MessageSquare, CheckSquare, ClipboardList, Clock, AlertTriangle, RotateCcw } from 'lucide-react';
import { tasksApi } from '../../api/tasks';
import ConfirmDialog, { ConfirmState } from '../../components/ui/ConfirmDialog';
import PageHeader from '../../components/PageHeader';
import { useFileDropPaste } from '../../hooks/useFileDropPaste';

// Partner-facing task page. This mirrors the layout of the normal TaskDetails
// page but renders the stripped /tasks/{id}/client-view payload — so the
// partner sees title, description, checklist, attachments, comments and
// activity, but NEVER sees internal assignees, preparer, reviewer, approval
// chain or department info. All actor names in activity/comments are replaced
// with the actor's company name on the backend.

interface TaskHeader {
  id: string;
  title: string;
  description?: string | null;
  status: string;
  priority?: string | null;
  due_date?: string | null;
  created_at?: string | null;
  last_activity_at?: string | null;
  completed_at?: string | null;
  progress_percent?: number | null;
  // True when caller is the originator AND status is still DRAFT.
  can_edit?: boolean;
  // True when caller is the originator (any status).
  can_delete?: boolean;
  // True when caller has archived this task from their own view.
  is_archived?: boolean;
}

interface CommentRow {
  id: string;
  commenter_name: string;
  comment: string;
  created_at?: string | null;
}

interface ActivityRow {
  id: string;
  action: string;
  message?: string | null;
  actor_name: string;
  created_at?: string | null;
}

interface AttachmentRow {
  id: string;
  file_name?: string | null;
  file_url?: string | null;
  file_size?: number | null;
  uploaded_by_name?: string | null;
  created_at?: string | null;
}

interface ChecklistRow {
  id: string;
  item: string;
  is_done: boolean;
  order_index: number;
}

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'bg-gray-100 text-gray-700 ring-gray-200',
  TODO: 'bg-blue-100 text-blue-700 ring-blue-200',
  IN_PROGRESS: 'bg-indigo-100 text-indigo-700 ring-indigo-200',
  IN_REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  REVIEW: 'bg-purple-100 text-purple-700 ring-purple-200',
  DONE: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  COMPLETED: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  CANCELLED: 'bg-red-100 text-red-700 ring-red-200',
};

const PRIORITY_BADGE: Record<string, string> = {
  URGENT: 'bg-red-100 text-red-700 ring-red-200',
  HIGH: 'bg-orange-100 text-orange-700 ring-orange-200',
  MEDIUM: 'bg-amber-100 text-amber-700 ring-amber-200',
  LOW: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
};

const ACTIVITY_LABEL: Record<string, string> = {
  CREATED: 'created this task',
  STATUS_CHANGED: 'updated the status',
  COMMENTED: 'commented',
  COMPLETED: 'marked the task complete',
  ATTACHMENT_ADDED: 'attached a file',
  DRAFT_APPROVED: 'approved the draft',
  DRAFT_REJECTED: 'rejected the draft',
  DRAFT_SENT_BACK: 'sent the draft back',
};

function fmtDate(s?: string | null) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return s;
  }
}

function fmtDateTime(s?: string | null) {
  if (!s) return '—';
  try {
    const d = new Date(s);
    return d.toLocaleString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return s;
  }
}

function fmtBytes(n?: number | null) {
  if (!n || n < 1024) return `${n || 0} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

type Tab = 'details' | 'checklist' | 'comments' | 'attachments' | 'activity';

const VALID_TABS: ReadonlyArray<Tab> = ['details', 'checklist', 'comments', 'attachments', 'activity'];

export default function PartnerTaskDetail() {
  const { id: taskId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Honor ?tab=activity (deep-linked from DRAFT_SEND_BACK / DRAFT_REJECTED
  // notifications) so the originator lands directly on the panel that
  // explains what happened.
  const initialTab: Tab = (() => {
    const q = (searchParams.get('tab') || '').toLowerCase();
    return (VALID_TABS as readonly string[]).includes(q) ? (q as Tab) : 'details';
  })();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [task, setTask] = useState<TaskHeader | null>(null);
  const [comments, setComments] = useState<CommentRow[]>([]);
  const [activity, setActivity] = useState<ActivityRow[]>([]);
  const [attachments, setAttachments] = useState<AttachmentRow[]>([]);
  const [checklist, setChecklist] = useState<ChecklistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState<{
    title: string;
    description: string;
    priority: string;
    due_date: string;
  }>({ title: '', description: '', priority: 'MEDIUM', due_date: '' });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!taskId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await tasksApi.getTaskClientView(taskId);
      setTask(res.data?.task || null);
      setComments(Array.isArray(res.data?.comments) ? res.data.comments : []);
      setActivity(Array.isArray(res.data?.activity) ? res.data.activity : []);
      setAttachments(Array.isArray(res.data?.attachments) ? res.data.attachments : []);
      setChecklist(Array.isArray(res.data?.checklist) ? res.data.checklist : []);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load task');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);

  const submitComment = async () => {
    const text = reply.trim();
    if (!text || posting || !taskId) return;
    setPosting(true);
    try {
      await tasksApi.postClientComment(taskId, text);
      setReply('');
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || err?.message || 'Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  const onPickFile = () => fileInputRef.current?.click();

  const uploadFiles = async (files: File[]) => {
    if (!files.length || uploading || !taskId) return;
    setUploading(true);
    try {
      for (const file of files) {
        await tasksApi.postClientAttachment(taskId, file);
      }
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || err?.message || 'Failed to upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onFileChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    await uploadFiles(files);
  };

  const { dropRef: attachmentDropRef, isDragOver: attachmentDragOver } = useFileDropPaste({
    onFiles: uploadFiles,
    disabled: tab !== 'attachments' || uploading || !taskId,
    pasteOnDocument: true,
  });

  const beginEdit = () => {
    if (!task) return;
    setEditForm({
      title: task.title || '',
      description: task.description || '',
      priority: (task.priority || 'MEDIUM').toUpperCase(),
      due_date: task.due_date ? task.due_date.slice(0, 10) : '',
    });
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const onArchive = () => {
    if (!taskId || !task) return;
    const currentlyArchived = !!task.is_archived;
    setConfirm({
      title: currentlyArchived ? 'Unarchive this task?' : 'Archive this task?',
      description: currentlyArchived
        ? 'It will reappear in your list.'
        : 'It will be hidden from your list. The other side will still see it.',
      confirmLabel: currentlyArchived ? 'Unarchive' : 'Archive',
      tone: 'default',
      onConfirm: async () => {
        if (currentlyArchived) {
          await tasksApi.unarchiveTaskForMe(taskId);
        } else {
          await tasksApi.archiveTaskForMe(taskId);
        }
        navigate(-1);
      },
    });
  };

  const onDelete = () => {
    if (!taskId) return;
    setConfirm({
      title: 'Delete this task?',
      description: 'It will move to Recently Deleted and disappear from both your and the partner’s lists.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        await tasksApi.deleteMyPartnerTask(taskId);
        navigate(-1);
      },
    });
  };

  const saveEdit = async () => {
    if (!taskId || saving) return;
    setSaving(true);
    try {
      await tasksApi.updateMyDraft(taskId, {
        title: editForm.title.trim() || undefined,
        description: editForm.description,
        priority: editForm.priority,
        due_date: editForm.due_date
          ? new Date(editForm.due_date).toISOString()
          : null,
      });
      setEditing(false);
      await load();
    } catch (err: any) {
      alert(err?.response?.data?.detail || err?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-500">Loading task…</div>
    );
  }
  if (error) {
    return (
      <div className="p-6">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 mb-4"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error}
        </div>
      </div>
    );
  }
  if (!task) {
    return <div className="p-6 text-center text-gray-500">Task not found.</div>;
  }

  const statusClass = STATUS_BADGE[(task.status || '').toUpperCase()] || 'bg-gray-100 text-gray-600 ring-gray-200';
  const priorityClass = PRIORITY_BADGE[(task.priority || '').toUpperCase()] || 'bg-gray-100 text-gray-600 ring-gray-200';

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <div className="mb-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ChevronLeft className="w-4 h-4" /> Back
        </button>
      </div>

      {/* Header */}
      <PageHeader
        eyebrow="III. Partner Task"
        title={<>{task.title?.split(' ')[0] || 'Task'} <em>{task.title?.split(' ').slice(1).join(' ') || 'Detail'}</em></>}
        subtitle={
          <span className="inline-flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${statusClass}`}>
              {(task.status || '—').replace(/_/g, ' ')}
            </span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1 ring-inset ${priorityClass}`}>
              {(task.priority || '—').toUpperCase()}
            </span>
            {task.due_date && (
              <span className="text-xs text-gray-500">Due <em>{fmtDate(task.due_date)}</em></span>
            )}
            {task.progress_percent != null && (
              <span className="text-xs text-gray-500">{task.progress_percent}% complete</span>
            )}
          </span>
        }
        actions={
          <>
            {task.can_edit && !editing && (
              <button
                type="button"
                onClick={() => { beginEdit(); setTab('details'); }}
                className="gt-btn is-primary"
              >
                Edit draft
              </button>
            )}
            <button
              type="button"
              onClick={onArchive}
              className="gt-btn"
            >
              {task.is_archived ? 'Unarchive' : 'Archive'}
            </button>
            {task.can_delete && (
              <button
                type="button"
                onClick={onDelete}
                className="gt-btn is-danger"
              >
                Delete
              </button>
            )}
          </>
        }
      />
      {task.can_edit && (
        <p className="text-xs text-gray-500 -mt-2 mb-2 italic">
          You can edit this task until the receiving admin approves it. After approval, only comments and attachments stay open.
        </p>
      )}

      {/* Tabs */}
      <div className="mt-5 border-b border-gray-200 flex items-center gap-1 overflow-x-auto">
        {([
          { id: 'details', label: 'Details', icon: ClipboardList },
          { id: 'checklist', label: `Checklist (${checklist.filter(c => c.is_done).length}/${checklist.length})`, icon: CheckSquare },
          { id: 'comments', label: `Comments (${comments.length})`, icon: MessageSquare },
          { id: 'attachments', label: `Attachments (${attachments.length})`, icon: Paperclip },
          { id: 'activity', label: `Activity (${activity.length})`, icon: Clock },
        ] as Array<{ id: Tab; label: string; icon: any }>).map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 whitespace-nowrap ${
                active
                  ? 'border-emerald-600 text-emerald-700'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Panels */}
      <div className="mt-4">
        {tab === 'details' && (
          editing && task.can_edit ? (
            <div className="rounded-xl border border-emerald-200 bg-white p-5 md:p-6 shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-4">Edit draft</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    value={editForm.title}
                    onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                    Description
                  </label>
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                    rows={5}
                    className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    placeholder="What needs to be done?"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                      Priority
                    </label>
                    <select
                      value={editForm.priority}
                      onChange={(e) => setEditForm((f) => ({ ...f, priority: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    >
                      <option value="URGENT">Urgent</option>
                      <option value="HIGH">High</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="LOW">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-1">
                      Due date
                    </label>
                    <input
                      type="date"
                      value={editForm.due_date}
                      onChange={(e) => setEditForm((f) => ({ ...f, due_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    disabled={saving || !editForm.title.trim()}
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
                  >
                    {saving ? 'Saving…' : 'Save changes'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">Description</h3>
              <p className="text-sm text-gray-800 whitespace-pre-wrap">
                {task.description?.trim() || (
                  <span className="text-gray-400 italic">No description provided.</span>
                )}
              </p>
              <hr className="my-5 border-gray-200" />
              <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 text-sm">
                <Field label="Created" value={fmtDateTime(task.created_at)} />
                <Field label="Last update" value={fmtDateTime(task.last_activity_at)} />
                {task.completed_at && <Field label="Completed" value={fmtDateTime(task.completed_at)} />}
              </dl>
            </div>
          )
        )}

        {tab === 'checklist' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            {checklist.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No checklist items.</p>
            ) : (
              <ul className="space-y-2">
                {checklist.map((c) => (
                  <li key={c.id} className="flex items-start gap-2 text-sm">
                    <span
                      className={`mt-0.5 inline-flex items-center justify-center w-4 h-4 rounded border ${
                        c.is_done
                          ? 'bg-emerald-500 border-emerald-500 text-white'
                          : 'border-gray-300'
                      }`}
                    >
                      {c.is_done && (
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span className={c.is_done ? 'line-through text-gray-500' : 'text-gray-800'}>
                      {c.item}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === 'comments' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            {comments.length === 0 ? (
              <p className="text-sm text-gray-500 italic mb-4">No comments yet. Be the first to reply.</p>
            ) : (
              <ul className="space-y-3 mb-4">
                {comments.map((c) => (
                  <li key={c.id} className="rounded-lg border border-gray-200 p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-semibold text-gray-900">{c.commenter_name}</span>
                      <span className="text-xs text-gray-500">{fmtDateTime(c.created_at)}</span>
                    </div>
                    <p className="text-sm text-gray-800 whitespace-pre-wrap">{c.comment}</p>
                  </li>
                ))}
              </ul>
            )}
            <div className="border-t border-gray-200 pt-3">
              <label htmlFor="reply" className="block text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                Post a reply
              </label>
              <textarea
                id="reply"
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={3}
                className="w-full border border-gray-300 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
                placeholder="Write a comment for the team…"
              />
              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  disabled={!reply.trim() || posting}
                  onClick={submitComment}
                  className="px-4 py-1.5 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {posting ? 'Posting…' : 'Post comment'}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === 'attachments' && (
          <div
            ref={attachmentDropRef}
            tabIndex={-1}
            className={`rounded-xl border bg-white p-5 md:p-6 shadow-sm outline-none transition-all ${
              attachmentDragOver
                ? 'border-emerald-400 ring-2 ring-emerald-300 ring-offset-2 bg-emerald-50/40'
                : 'border-gray-200'
            }`}
          >
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold">
                {attachments.length} file{attachments.length === 1 ? '' : 's'}
              </h3>
              <button
                type="button"
                onClick={onPickFile}
                disabled={uploading}
                className="text-xs font-semibold text-emerald-700 hover:text-emerald-800 disabled:opacity-50"
              >
                {uploading ? 'Uploading…' : '+ Attach file'}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={onFileChosen}
              />
            </div>
            <p className="text-[11px] text-gray-500 mb-3">
              Drag &amp; drop or paste with Ctrl/Cmd+V to attach.
            </p>
            {attachments.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No attachments yet.</p>
            ) : (
              <ul className="space-y-2">
                {attachments.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center gap-3 text-sm border border-gray-200 rounded-lg p-2.5"
                  >
                    <div className="flex-1 min-w-0">
                      {a.file_url ? (
                        <a
                          href={a.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="font-medium text-emerald-700 hover:underline truncate block"
                        >
                          {a.file_name || 'file'}
                        </a>
                      ) : (
                        <span className="font-medium text-gray-900 truncate block">
                          {a.file_name || 'file'}
                        </span>
                      )}
                      <div className="text-xs text-gray-500">
                        {fmtBytes(a.file_size)} · {a.uploaded_by_name || 'Team'} · {fmtDate(a.created_at)}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {tab === 'activity' && (
          <div className="rounded-xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
            {activity.length === 0 ? (
              <p className="text-sm text-gray-500 italic">No activity yet.</p>
            ) : (
              <ul className="space-y-3">
                {activity.map((a) => {
                  const isSendBack = a.action === 'DRAFT_SENT_BACK';
                  const isRejected = a.action === 'DRAFT_REJECTED';
                  const isCallout = isSendBack || isRejected;
                  const calloutBg = isRejected ? '#fef2f2' : '#fff7ed';
                  const calloutBorder = isRejected ? '#fecaca' : '#fed7aa';
                  const calloutFg = isRejected ? '#991b1b' : '#9a3412';
                  const Icon = isRejected ? AlertTriangle : RotateCcw;

                  if (isCallout) {
                    return (
                      <li
                        key={a.id}
                        className="rounded-xl p-3.5 flex items-start gap-3"
                        style={{
                          background: calloutBg,
                          border: `1px solid ${calloutBorder}`,
                        }}
                      >
                        <span
                          className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full"
                          style={{ background: '#ffffff', color: calloutFg }}
                        >
                          <Icon className="w-4 h-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <span className="text-sm" style={{ color: calloutFg }}>
                              <span className="font-semibold">{a.actor_name}</span>{' '}
                              {ACTIVITY_LABEL[a.action] || a.action.toLowerCase().replace(/_/g, ' ')}
                            </span>
                            <span className="text-[11px] text-gray-500 whitespace-nowrap gt-numerals">
                              {fmtDateTime(a.created_at)}
                            </span>
                          </div>
                          {a.message && (
                            <p
                              className="text-sm mt-1.5 whitespace-pre-wrap"
                              style={{ color: '#1f2937' }}
                            >
                              <span
                                className="gt-eyebrow"
                                style={{ marginBottom: 4, display: 'block', color: calloutFg }}
                              >
                                {isRejected ? 'Reason for rejection' : 'What to fix'}
                              </span>
                              {a.message}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  }

                  return (
                    <li key={a.id} className="text-sm text-gray-700 flex gap-2">
                      <span className="text-gray-400 text-xs whitespace-nowrap mt-0.5 gt-numerals">
                        {fmtDateTime(a.created_at)}
                      </span>
                      <span>
                        <span className="font-medium text-gray-900">{a.actor_name}</span>{' '}
                        <span className="text-gray-600">— {ACTIVITY_LABEL[a.action] || a.action.toLowerCase().replace(/_/g, ' ')}</span>
                        {a.message && <span className="text-gray-500 italic"> · {a.message}</span>}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[10px] uppercase tracking-wide text-gray-400 font-semibold">{label}</dt>
      <dd className="text-gray-900 mt-0.5">{value}</dd>
    </div>
  );
}
